"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Menu, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import Image from "next/image";
import logoRomano from "@/public/assets/romano-logo.png";

export function SiteHeader() {
  const pathname = usePathname();
  const router = useRouter();
  const isHome = pathname === "/";
  const isProducts = pathname === "/products" || pathname.startsWith("/products/");
  const isAbout = pathname === "/about-romano";
  const isArticles = pathname === "/articles" || pathname.startsWith("/articles/");
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  const closeMobile = React.useCallback(() => {
    setMobileOpen(false);
  }, []);

  React.useEffect(() => {
    closeMobile();
  }, [closeMobile, pathname]);

  React.useEffect(() => {
    const hrefs = ["/products", "/articles", "/about-romano", "/buy-now"];
    const callback = () => {
      hrefs.forEach((href) => router.prefetch(href));
    };

    if (typeof window === "undefined") return;
    const ric = window.requestIdleCallback;
    if (typeof ric === "function") {
      const id = ric(callback, { timeout: 1500 });
      return () => window.cancelIdleCallback?.(id);
    }

    const id = window.setTimeout(callback, 0);
    return () => window.clearTimeout(id);
  }, [router]);

  React.useEffect(() => {
    if (!mobileOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMobile();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [closeMobile, mobileOpen]);

  React.useEffect(() => {
    if (!isHome) {
      setScrolled(true);
      return;
    }

    const update = () => {
      setScrolled(window.scrollY > 8);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });

    return () => {
      window.removeEventListener("scroll", update);
    };
  }, [isHome]);

  const isTransparent = isHome && !scrolled;

  const headerLinkClassName = isTransparent
    ? "h-9 rounded-full px-2 text-[16px] font-normal leading-none tracking-normal text-white/85 no-underline transition-[background-color,color] duration-[400ms] ease-out hover:bg-white/10 hover:text-white hover:no-underline"
    : "h-9 rounded-full px-2 text-[16px] font-normal leading-none tracking-normal text-white/75 no-underline transition-[background-color,color] duration-[400ms] ease-out hover:bg-white/10 hover:text-white hover:no-underline";

  const activeLinkClassName =
    "h-9 rounded-full bg-white px-4 text-[16px] font-normal leading-none tracking-normal text-primary no-underline transition-[background-color,color] duration-[400ms] ease-out hover:bg-white/90 hover:no-underline";

  const productsLinkClassName = isProducts
    ? "h-9 rounded-full bg-white px-4 text-[16px] font-normal leading-none tracking-normal text-primary no-underline hover:bg-white/90 hover:no-underline"
    : headerLinkClassName;

  const aboutLinkClassName = isAbout ? activeLinkClassName : headerLinkClassName;
  const articlesLinkClassName = isArticles ? activeLinkClassName : headerLinkClassName;

  const mobileMenuItemClassName = isTransparent
    ? "h-11 justify-start rounded-xl text-[clamp(13px,1.15vw,15px)] font-normal text-foreground/85 transition-[background-color,color] duration-[400ms] ease-out hover:bg-black/5 hover:text-foreground"
    : "h-11 justify-start rounded-xl text-[clamp(13px,1.15vw,15px)] font-normal text-white/90 transition-[background-color,color] duration-[400ms] ease-out hover:bg-white/10 hover:text-white";

  const mobileMenuItems = [
    { href: "/products", label: "Products" },
    { href: "/about-romano", label: "About Romano" },
    { href: "/articles", label: "News & Articles" },
  ];

  return (
    <header
      className={
        isTransparent
          ? "fixed top-0 z-1000 w-full bg-transparent"
          : "fixed top-0 z-1000 w-full border-b border-primary/10 bg-primary backdrop-blur"
      }
    >
      <div className="mx-auto grid w-full grid-cols-[1fr_auto_1fr] items-center justify-center px-5 py-4">
        <div className="flex items-center gap-2 justify-self-start">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className={
              isTransparent
                ? "md:hidden text-primary hover:bg-black/5 hover:text-foreground"
                : "md:hidden text-white/85 hover:bg-white/10 hover:text-white"
            }
            onClick={() => setMobileOpen((value) => !value)}
            aria-label="Open menu"
            aria-controls="mobile-menu"
            aria-expanded={mobileOpen}
          >
            <Menu />
          </Button>

          <nav className="hidden items-center gap-1 md:flex">
            <Button
              asChild
              variant="link"
              className={productsLinkClassName}
            >
              <Link href="/products" aria-current={isProducts ? "page" : undefined}>
                PRODUCTS
              </Link>
            </Button>
            <Button
              asChild
              variant="link"
              className={aboutLinkClassName}
            >
              <Link href="/about-romano" aria-current={isAbout ? "page" : undefined}>
                ABOUT ROMANO
              </Link>
            </Button>
            <Button
              asChild
              variant="link"
              className={articlesLinkClassName}
            >
              <Link href="/articles" prefetch aria-current={isArticles ? "page" : undefined}>
                NEWS & ARTICLES
              </Link>
            </Button>
          </nav>
        </div>

        <Link
          href="/"
          className="flex flex-col items-center gap-1 text-[clamp(12px,1vw,14px)] font-semibold tracking-[0.28em]"
          aria-label="Romano Home"
        >
          <Image
            src={logoRomano}
            alt="Romano Logo"
            className={
              isTransparent
                ? "h-[clamp(2.75rem,6vw,4rem)] w-auto"
                : "h-[clamp(2.75rem,6vw,4rem)] w-auto brightness-0 invert"
            }
            priority
          />
        </Link>

        <div className="justify-self-end">
          <Button
            asChild
            size="sm"
            variant="outline"
            className={
              isTransparent
                ? "hidden md:inline-flex rounded-full border-border/70 bg-transparent text-[clamp(12px,1.05vw,16px)] font-normal tracking-[0.18em] text-white hover:bg-black/5 hover:text-white/90"
                : "hidden md:inline-flex rounded-full border-white/70 bg-transparent text-[clamp(12px,1.05vw,16px)] font-normal tracking-[0.18em] text-white hover:bg-white/10 hover:text-white"
            }
          >
            <Link href="/buy-now">BUY NOW</Link>
          </Button>
        </div>
      </div>

      {mobileOpen ? (
        <div className="md:hidden">
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={closeMobile}
            aria-hidden
          />
          <div
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            className="fixed inset-x-4 top-20 z-50 rounded-2xl border border-white/10 bg-[#363636] p-4 shadow-lg backdrop-blur"
          >
            <div className="flex items-center justify-between">
              <p className="text-[clamp(12px,1vw,14px)] font-semibold tracking-[0.22em] text-white/70">
                MENU
              </p>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="text-white/85 hover:bg-white/10 hover:text-white"
                onClick={closeMobile}
                aria-label="Close menu"
              >
                <X />
              </Button>
            </div>

            <div className="mt-3 grid gap-1">
              {mobileMenuItems.map((item) => {
                const isActive =
                  item.href === "/products"
                    ? isProducts
                    : item.href === "/articles"
                      ? isArticles
                      : pathname === item.href;
                return (
                  <Button
                    key={item.href}
                    asChild
                    variant="ghost"
                    className={`${mobileMenuItemClassName} ${isActive ? "bg-white/10 text-white" : "text-white"}`}
                    onClick={closeMobile}
                  >
                    <Link href={item.href} aria-current={isActive ? "page" : undefined}>
                      {item.label}
                    </Link>
                  </Button>
                );
              })}
            </div>

            <div className="mt-4">
              <Button
                asChild
                variant="outline"
                className="h-11 w-full rounded-full border-white/70 bg-transparent text-[clamp(12px,1.05vw,14px)] font-semibold tracking-[0.18em] text-white hover:bg-white/10 hover:text-white"
                onClick={closeMobile}
              >
                <Link href="/buy-now">BUY NOW</Link>
              </Button>
            </div>
          </div>
        </div>
      ) : null}

    </header>
  );
}
